import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";

const input = "mt-1 w-full rounded-md border border-linen bg-ivory px-3 py-2 text-sm outline-none focus:border-forest";
const label = "text-xs font-semibold uppercase tracking-[0.15em] text-moss";
const btn = "rounded-md bg-forest px-4 py-2 text-sm font-semibold text-ivory transition-colors hover:bg-forest-dark disabled:opacity-50";
export const PAYMENT_LABELS = { cash: "Cash", card: "Card", gift_certificate: "Gift certificate", banked_credit: "Banked credit", other: "Other" };

export function SaleForm({ onSaved, onCancel }) {
  const [menu, setMenu] = useState([]);
  const [products, setProducts] = useState([]);
  const [patients, setPatients] = useState([]);
  const [sale, setSale] = useState({ date: new Date().toISOString().slice(0, 10), client_email: "", client_name: "", discount: "", payment_method: "card", note: "" });
  const [items, setItems] = useState([]);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/menu").then((r) => setMenu(r.data));
    api.get("/admin/inventory").then((r) => setProducts(r.data));
    api.get("/admin/patients").then((r) => setPatients(r.data));
  }, []);

  const catalog = useMemo(() => [
    ...menu.flatMap((c) => c.items.map((i) => ({ key: `service:${i.id}`, kind: "service", ref_id: i.id, name: i.name, price: i.price, group: c.category }))),
    ...products.map((p) => ({ key: `product:${p.id}`, kind: "product", ref_id: p.id, name: p.name, price: p.retail_price, group: `Products · ${p.category}`, stock: p.quantity })),
  ], [menu, products]);
  const groups = useMemo(() => Object.entries(catalog.reduce((acc, c) => ({ ...acc, [c.group]: [...(acc[c.group] || []), c] }), {})), [catalog]);

  const addPick = (key) => {
    if (!key) return;
    if (key === "custom") { setItems([...items, { kind: "custom", ref_id: null, name: "", qty: 1, unit_price: "" }]); }
    else { const c = catalog.find((x) => x.key === key); setItems([...items, { kind: c.kind, ref_id: c.ref_id, name: c.name, qty: 1, unit_price: c.price }]); }
    setPick("");
  };
  const upd = (i, k, v) => setItems(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const subtotal = items.reduce((s, i) => s + Number(i.qty || 0) * Number(i.unit_price || 0), 0);
  const total = Math.max(subtotal - Number(sale.discount || 0), 0);

  const pickClient = (email) => {
    const p = patients.find((x) => x.email === email);
    setSale({ ...sale, client_email: email, client_name: p ? p.name : sale.client_name });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return toast.error("Add at least one item");
    setBusy(true);
    try {
      const payload = { ...sale, client_email: sale.client_email || null, discount: Number(sale.discount || 0), items: items.map((i) => ({ ...i, qty: Number(i.qty), unit_price: Number(i.unit_price || 0) })) };
      const { data } = await api.post("/admin/sales", payload);
      toast.success(`Sale ${data.number} recorded — $${data.total.toFixed(2)}`);
      onSaved();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid="sale-form" className="rounded-lg border border-linen bg-ivory p-5">
      <div className="flex items-center justify-between"><h3 className="font-serif text-xl text-forest">New sale</h3><button type="button" onClick={onCancel} className="text-moss hover:text-forest" aria-label="Close"><X size={18} /></button></div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div><label className={label}>Date</label><input data-testid="sale-date-input" type="date" required value={sale.date} onChange={(e) => setSale({ ...sale, date: e.target.value })} className={input} /></div>
        <div><label className={label}>Patient account (optional)</label>
          <select data-testid="sale-client-select" value={sale.client_email} onChange={(e) => pickClient(e.target.value)} className={input}><option value="">Walk-in / no account</option>{patients.map((p) => <option key={p.email} value={p.email}>{p.name} — {p.email}</option>)}</select>
        </div>
        <div><label className={label}>Client name</label><input data-testid="sale-client-name-input" value={sale.client_name} onChange={(e) => setSale({ ...sale, client_name: e.target.value })} placeholder="Jane D." className={input} /></div>
        <div><label className={label}>Payment</label><select data-testid="sale-payment-select" value={sale.payment_method} onChange={(e) => setSale({ ...sale, payment_method: e.target.value })} className={input}>{Object.entries(PAYMENT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
      </div>

      <div className="mt-5">
        <label className={label}>Add item</label>
        <select data-testid="sale-add-item-select" value={pick} onChange={(e) => addPick(e.target.value)} className={input}>
          <option value="">Choose a service or product…</option>
          {groups.map(([g, list]) => <optgroup key={g} label={g}>{list.map((c) => <option key={c.key} value={c.key}>{c.name} — ${c.price}{c.stock != null ? ` (${c.stock} in stock)` : ""}</option>)}</optgroup>)}
          <option value="custom">Custom line item…</option>
        </select>
      </div>
      <ul className="mt-3 divide-y divide-linen">
        {items.map((it, i) => (
          <li key={i} data-testid={`sale-line-${i}`} className="grid gap-2 py-2 sm:grid-cols-[1fr_90px_120px_110px_32px] sm:items-center">
            <input data-testid={`sale-line-name-${i}`} required value={it.name} onChange={(e) => upd(i, "name", e.target.value)} placeholder="Description" readOnly={it.kind !== "custom"} className={`${input} mt-0 ${it.kind !== "custom" ? "bg-cream/60" : ""}`} />
            <input data-testid={`sale-line-qty-${i}`} type="number" min="0.01" step="any" required value={it.qty} onChange={(e) => upd(i, "qty", e.target.value)} className={`${input} mt-0`} />
            <input data-testid={`sale-line-price-${i}`} type="number" min="0" step="0.01" required value={it.unit_price} onChange={(e) => upd(i, "unit_price", e.target.value)} placeholder="Price" className={`${input} mt-0`} />
            <span className="text-right text-sm font-semibold text-forest">${(Number(it.qty || 0) * Number(it.unit_price || 0)).toFixed(2)}</span>
            <button type="button" onClick={() => setItems(items.filter((_, idx) => idx !== i))} className="text-moss hover:text-destructive" aria-label="Remove"><Trash2 size={15} /></button>
          </li>
        ))}
        {items.length === 0 && <li className="py-3 text-xs text-moss">No items yet.</li>}
      </ul>

      <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_160px_160px] sm:items-end">
        <div><label className={label}>Note</label><input data-testid="sale-note-input" value={sale.note} onChange={(e) => setSale({ ...sale, note: e.target.value })} className={input} /></div>
        <div><label className={label}>Discount ($)</label><input data-testid="sale-discount-input" type="number" min="0" step="0.01" value={sale.discount} onChange={(e) => setSale({ ...sale, discount: e.target.value })} className={input} /></div>
        <div className="text-right"><p className={label}>Total</p><p data-testid="sale-total" className="font-serif text-3xl text-forest">${total.toFixed(2)}</p></div>
      </div>
      <button type="submit" data-testid="sale-save-button" disabled={busy} className={`${btn} mt-4 flex items-center gap-2`}><Plus size={14} /> {busy ? "Saving..." : "Record Sale"}</button>
    </form>
  );
}
